import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Facebook, Plus, Trash2, RefreshCw, Save, ExternalLink, Users, Sparkles, UploadCloud, X, Image as ImageIcon, Loader2 } from "lucide-react";

const MAX_COVER_MB = 6;
const ACCEPTED_COVER = ["image/jpeg", "image/png", "image/webp"];

// Upload d'une image de couverture (remplace le champ URL) + aperçu
function CoverUpload({ value, onChange, testId = "cover-upload" }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const pick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ACCEPTED_COVER.includes(file.type)) { toast.error("Format non supporté (JPG, PNG ou WEBP)"); return; }
    if (file.size > MAX_COVER_MB * 1024 * 1024) { toast.error(`Image trop lourde (max ${MAX_COVER_MB} Mo)`); return; }
    upload(file);
  };

  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/facebook-groups/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image téléchargée");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec du téléchargement");
    } finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-3" data-testid={testId}>
      <div className="w-16 h-16 shrink-0 rounded-xl overflow-hidden border border-slate-200 bg-slate-50 grid place-items-center">
        {value ? (
          <img src={value} alt="Couverture" className="w-full h-full object-cover" data-testid={`${testId}-preview`} />
        ) : (
          <ImageIcon size={20} className="text-slate-300" />
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid={`${testId}-input`} onChange={pick} />
        <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()} data-testid={`${testId}-btn`}>
          {busy ? <Loader2 size={13} className="mr-1 animate-spin" /> : <UploadCloud size={13} className="mr-1" />}
          {busy ? "Envoi…" : value ? "Remplacer" : "Télécharger une image"}
        </Button>
        {value && (
          <button type="button" onClick={() => onChange("")} data-testid={`${testId}-remove`} className="inline-flex items-center gap-1 text-[11px] text-rose-600 hover:underline">
            <X size={11} /> Retirer l'image
          </button>
        )}
      </div>
    </div>
  );
}

const EMPTY = {
  name: "",
  url: "",
  description: "",
  department: "Eure-et-Loir (28)",
  region: "Centre-Val de Loire",
  city: "",
  image_url: "",
  member_count: 0,
  display_order: 0,
  active: true,
};

function formatMembers(n) {
  const v = Number(n || 0);
  if (v >= 1000) return `${(v / 1000).toFixed(v >= 10000 ? 0 : 1).replace(".0", "")}k`;
  return v.toLocaleString("fr-FR");
}

export default function AdminFacebookGroups() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [disp, setDisp] = useState({ members_display: "", groups_display: "", reassurance_text: "" });
  const [savingDisp, setSavingDisp] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/facebook-groups");
      setItems(data.items || []);
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/admin/fb-display")
      .then((r) => setDisp({ members_display: r.data.members_display || "", groups_display: r.data.groups_display || "", reassurance_text: r.data.reassurance_text || "" }))
      .catch(() => {});
  }, []);

  const saveDisp = async () => {
    setSavingDisp(true);
    try {
      const { data } = await api.put("/admin/fb-display", disp);
      setDisp({ members_display: data.members_display || "", groups_display: data.groups_display || "", reassurance_text: data.reassurance_text || "" });
      toast.success("Chiffres affichés mis à jour");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setSavingDisp(false); }
  };

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const createGroup = async (e) => {
    e?.preventDefault();
    if (!form.url) { toast.error("URL requise"); return; }
    setCreating(true);
    try {
      await api.post("/admin/facebook-groups", form);
      toast.success("Groupe ajouté");
      setForm(EMPTY);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setCreating(false); }
  };

  const updateField = async (id, patch) => {
    setBusyId(id);
    try {
      const { data } = await api.patch(`/admin/facebook-groups/${id}`, patch);
      setItems((arr) => arr.map((g) => g.id === id ? data.item : g));
      toast.success("Enregistré");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setBusyId(""); }
  };

  const saveRow = async (row) => {
    const { id, name, url, description, department, region, city, image_url, member_count, display_order, active } = row;
    await updateField(id, { name, url, description, department, region, city, image_url, member_count: Number(member_count || 0), display_order: Number(display_order || 0), active });
  };

  const delRow = async (row) => {
    if (!confirm(`Supprimer le groupe « ${row.name || row.url} » ?`)) return;
    setBusyId(row.id);
    try {
      await api.delete(`/admin/facebook-groups/${row.id}`);
      setItems((arr) => arr.filter((g) => g.id !== row.id));
      toast.success("Supprimé");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setBusyId(""); }
  };

  const refreshRow = async (row) => {
    setBusyId(row.id);
    try {
      const { data } = await api.post(`/admin/facebook-groups/${row.id}/refresh-members`);
      if (data.ok) {
        setItems((arr) => arr.map((g) => g.id === row.id ? data.item : g));
        toast.success(`Nombre de membres mis à jour : ${data.count}`);
      } else {
        toast.warning(data.message || "Facebook n'a pas exposé le nombre. Saisissez-le manuellement.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setBusyId(""); }
  };

  const bulkRefresh = async () => {
    if (!confirm("Rafraîchir automatiquement tous les groupes actifs ?")) return;
    setBulkLoading(true);
    try {
      const { data } = await api.post("/admin/facebook-groups/bulk-refresh");
      toast.success(`${data.updated} mis à jour · ${data.failed} indisponibles`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setBulkLoading(false); }
  };

  const patchLocal = (id, k, v) => setItems((arr) => arr.map((g) => g.id === id ? { ...g, [k]: v } : g));

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2" data-testid="admin-fb-title">
            <Facebook className="text-blue-600" size={26}/> Groupes Facebook
          </h1>
          <p className="text-slate-500 text-xs md:text-sm mt-1">Réseau de communautés locales affiché publiquement sur <code className="text-[11px] bg-slate-100 px-1 rounded">/groupes-facebook</code>.</p>
        </div>
        <Button disabled={bulkLoading} onClick={bulkRefresh} variant="outline" data-testid="admin-fb-bulk-refresh">
          <RefreshCw size={14} className={`mr-1 ${bulkLoading ? "animate-spin" : ""}`}/> {bulkLoading ? "…" : "Rafraîchir tous les membres"}
        </Button>
      </div>

      {/* Chiffres affichés publiquement (pilotables) */}
      <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-5" data-testid="admin-fb-display">
        <div className="flex items-center gap-2 mb-1"><Users size={18} className="text-emerald-600"/><h2 className="font-heading text-lg font-bold">Chiffres affichés publiquement</h2></div>
        <p className="text-xs text-slate-500 mb-3">Ces valeurs apparaissent sur la page d'accueil, la page Groupes Facebook et la page partenaire. Laissez « Nombre de groupes » vide pour afficher automatiquement le nombre réel de groupes actifs.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Membres en cumulé (affichage)</Label><Input value={disp.members_display} onChange={(e)=>setDisp((s)=>({ ...s, members_display: e.target.value }))} placeholder="120 000" data-testid="admin-fb-display-members"/></div>
          <div><Label>Nombre de groupes (affichage, optionnel)</Label><Input value={disp.groups_display} onChange={(e)=>setDisp((s)=>({ ...s, groups_display: e.target.value }))} placeholder="(auto)" data-testid="admin-fb-display-groups"/></div>
        </div>
        <div className="mt-3"><Label>Texte du bandeau de réassurance (accueil)</Label><Input value={disp.reassurance_text} onChange={(e)=>setDisp((s)=>({ ...s, reassurance_text: e.target.value }))} placeholder="100 % local · Eure-et-Loir" data-testid="admin-fb-display-reassurance"/></div>
        <div className="mt-3 flex justify-end"><Button onClick={saveDisp} disabled={savingDisp} className="btn-shine text-white" data-testid="admin-fb-display-save"><Save size={14} className="mr-1"/>{savingDisp ? "…" : "Enregistrer les chiffres"}</Button></div>
      </div>

      {/* Ajout */}
      <form onSubmit={createGroup} className="mt-6 bg-white border border-slate-200 rounded-2xl p-5" data-testid="admin-fb-create">
        <div className="flex items-center gap-2 mb-3">
          <Plus size={18} className="text-emerald-600"/>
          <h2 className="font-heading text-lg font-bold">Ajouter un groupe</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          <div className="md:col-span-2"><Label>URL Facebook *</Label><Input required value={form.url} onChange={(e)=>set("url", e.target.value)} placeholder="https://www.facebook.com/groups/…" data-testid="admin-fb-new-url"/></div>
          <div><Label>Nom</Label><Input value={form.name} onChange={(e)=>set("name", e.target.value)} placeholder="Bons plans Chartres" data-testid="admin-fb-new-name"/></div>
          <div><Label>Ville</Label><Input value={form.city} onChange={(e)=>set("city", e.target.value)}/></div>
          <div><Label>Département</Label><Input value={form.department} onChange={(e)=>set("department", e.target.value)}/></div>
          <div><Label>Région</Label><Input value={form.region} onChange={(e)=>set("region", e.target.value)}/></div>
          <div className="md:col-span-3"><Label>Image de couverture</Label><div className="mt-1"><CoverUpload value={form.image_url} onChange={(u)=>set("image_url", u)} testId="admin-fb-new-cover"/></div></div>
          <div><Label>Nb membres</Label><Input type="number" min="0" value={form.member_count} onChange={(e)=>set("member_count", Number(e.target.value||0))}/></div>
          <div><Label>Ordre</Label><Input type="number" value={form.display_order} onChange={(e)=>set("display_order", Number(e.target.value||0))}/></div>
          <div className="md:col-span-3"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e)=>set("description", e.target.value)} placeholder="Communauté locale animée par PROXIreduc-28…"/></div>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active} onChange={(e)=>set("active", e.target.checked)}/> Actif (visible publiquement)
          </label>
          <Button type="submit" disabled={creating} className="btn-shine text-white" data-testid="admin-fb-new-submit">
            <Plus size={14} className="mr-1"/> {creating ? "…" : "Ajouter le groupe"}
          </Button>
        </div>
      </form>

      {/* Liste */}
      <div className="mt-8">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={16} className="text-[#E05638]"/>
          <h2 className="font-heading text-xl font-bold">{items.length} groupe{items.length > 1 ? "s" : ""}</h2>
        </div>
        {loading ? (
          <div className="text-slate-500 text-sm">Chargement…</div>
        ) : items.length === 0 ? (
          <div className="text-slate-500 text-sm text-center py-12 bg-white border border-dashed rounded-2xl">Aucun groupe pour l'instant.</div>
        ) : (
          <div className="space-y-3">
            {items.map((g) => (
              <div key={g.id} className={`bg-white border rounded-2xl p-4 ${g.active ? "border-slate-200" : "border-slate-200 opacity-70"}`} data-testid={`admin-fb-row-${g.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="w-9 h-9 rounded-lg bg-blue-50 text-blue-700 grid place-items-center"><Facebook size={16}/></span>
                    <div>
                      <div className="font-heading font-bold text-slate-900">{g.name || "(sans nom)"}</div>
                      <a href={g.url} target="_blank" rel="noreferrer" className="text-[11px] text-slate-500 hover:text-[#E05638] inline-flex items-center gap-1">
                        <ExternalLink size={11}/> {g.url}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-bold" title={g.member_count_updated_at ? `MAJ ${new Date(g.member_count_updated_at).toLocaleString("fr-FR")} · source ${g.member_count_source}` : "Aucune mise à jour"}>
                      <Users size={12}/> {formatMembers(g.member_count)}
                    </span>
                    <label className="flex items-center gap-1 text-xs cursor-pointer">
                      <input type="checkbox" checked={!!g.active} onChange={(e)=>patchLocal(g.id, "active", e.target.checked)} className="w-3.5 h-3.5"/> Actif
                    </label>
                    <Button size="sm" variant="outline" onClick={()=>refreshRow(g)} disabled={busyId === g.id} title="Rafraîchir (best-effort)" data-testid={`admin-fb-refresh-${g.id}`}>
                      <RefreshCw size={13} className={busyId === g.id ? "animate-spin" : ""}/>
                    </Button>
                    <Button size="sm" onClick={()=>saveRow(g)} disabled={busyId === g.id} data-testid={`admin-fb-save-${g.id}`}><Save size={13}/></Button>
                    <Button size="sm" variant="outline" onClick={()=>delRow(g)} disabled={busyId === g.id} className="text-rose-600 hover:bg-rose-50" data-testid={`admin-fb-delete-${g.id}`}><Trash2 size={13}/></Button>
                  </div>
                </div>
                <div className="mt-3 grid md:grid-cols-3 gap-2">
                  <Input value={g.name || ""} onChange={(e)=>patchLocal(g.id, "name", e.target.value)} placeholder="Nom"/>
                  <Input value={g.url || ""} onChange={(e)=>patchLocal(g.id, "url", e.target.value)} placeholder="URL"/>
                  <Input value={g.city || ""} onChange={(e)=>patchLocal(g.id, "city", e.target.value)} placeholder="Ville"/>
                  <Input value={g.department || ""} onChange={(e)=>patchLocal(g.id, "department", e.target.value)} placeholder="Département"/>
                  <Input value={g.region || ""} onChange={(e)=>patchLocal(g.id, "region", e.target.value)} placeholder="Région"/>
                  <div className="md:col-span-3"><Label className="text-xs text-slate-500">Image de couverture</Label><div className="mt-1"><CoverUpload value={g.image_url} onChange={(u)=>patchLocal(g.id, "image_url", u)} testId={`admin-fb-cover-${g.id}`}/></div></div>
                  <Input type="number" value={g.member_count || 0} onChange={(e)=>patchLocal(g.id, "member_count", Number(e.target.value||0))} placeholder="Nb membres"/>
                  <Input type="number" value={g.display_order || 0} onChange={(e)=>patchLocal(g.id, "display_order", Number(e.target.value||0))} placeholder="Ordre"/>
                  <Textarea rows={1} className="md:col-span-3" value={g.description || ""} onChange={(e)=>patchLocal(g.id, "description", e.target.value)} placeholder="Description (laisser vide = auto)"/>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
